import React, { useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Section, Sub, Badge, PRI_TONE, STATUS_LABEL } from './GrowthBits';
import { PHASES, phasePct, isDone } from './growthData';

const fmtDue = (d) => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

export const TaskRow = ({ t, S, onToggle, showPhase, delay = 0 }) => {
  const done = isDone(S, t);
  const overdue = !done && new Date(t.due) < new Date();
  const [stTone, stLabel] = STATUS_LABEL[done ? 'completed' : t.status] || STATUS_LABEL.not_started;
  return (
    <div className={cn('egt-task', done && 'done', overdue && 'overdue')} style={{ animationDelay: `${delay}ms` }} data-testid={`egt-task-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>
      <button type="button" className={cn('egt-chk', done && 'on')} onClick={() => onToggle(t)} aria-pressed={done} aria-label={done ? 'Mark not done' : 'Mark done'}><Check /></button>
      <div style={{ minWidth: 0 }}>
        <b>{t.name}</b>
        <small>{t.dept} · {overdue ? 'Overdue — was due' : 'due'} {fmtDue(t.due)}{showPhase ? ` · ${t.phase}` : ''}</small>
      </div>
      <div className="meta"><Badge tone={PRI_TONE[t.pri]}>{t.pri}</Badge><Badge tone={stTone}>{stLabel}</Badge></div>
    </div>
  );
};

const Phase = ({ ph, S, open, onOpen, onToggle }) => {
  const pct = phasePct(S, ph);
  const status = pct === 100 ? ['green', 'Completed'] : pct > 0 ? ['blue', 'In progress'] : ['grey', 'Planned'];
  return (
    <div className={cn('egt-ph', open && 'open')} data-testid={`egt-phase-${ph.id}`}>
      <button type="button" className="egt-phh" onClick={onOpen} aria-expanded={open}>
        <span className="egt-phid">{ph.id}</span>
        <span style={{ minWidth: 0 }}><div className="egt-phname">{ph.name}</div><div className="egt-phwhen">{ph.when} · {ph.dept}</div></span>
        <span className="egt-phprog"><span><span>Progress</span><b>{pct}%</b></span><div className="gpj-wbar"><i style={{ width: `${pct}%` }} className={pct === 100 ? 'done' : ''} /></div></span>
        <Badge tone={status[0]}>{status[1]}</Badge>
        <ChevronRight className="egt-phcaret" />
      </button>
      {open && (
        <div className="egt-phb">
          <p className="egt-phsum">{ph.summary}</p>
          <div className="egt-phgrid">
            <div><div className="egt-lab">Key objectives</div><ul className="egt-obj">{ph.objectives.map((o) => <li key={o}>{o}</li>)}</ul></div>
            <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
              <div><div className="egt-lab">Completion criteria</div><div className="egt-crit">{ph.criteria}</div></div>
              <div><div className="egt-lab">Impact</div><div className="egt-crit">Advances the business toward the $40M–$50M/month target.</div></div>
            </div>
          </div>
          <div><div className="egt-lab">Milestone tasks</div><div className="egt-tasks">{ph.tasks.map((t, i) => <TaskRow key={t.name} t={t} S={S} onToggle={onToggle} delay={i * 60} />)}</div></div>
        </div>
      )}
    </div>
  );
};

export default function TimelineTab({ S, onToggle }) {
  const [open, setOpen] = useState(() => PHASES.find((p) => phasePct(S, p) < 100)?.id || 'P1');
  const total = PHASES.reduce((s, p) => s + p.tasks.length, 0), done = PHASES.reduce((s, p) => s + p.tasks.filter((t) => isDone(S, t)).length, 0);
  return (
    <div data-testid="egt-tab-timeline">
      <Section eyebrow="Part 01 · Live Growth Timeline" title="Six phases to a scalable $50M+ business" lead="The journey from foundation to a self-sustaining $50M+/month operation. Expand a phase for objectives, completion criteria and milestone tasks. Progress is computed from task completion." right={<span className="gpj-pill" data-testid="egt-timeline-total"><span className="gpj-live-dot" style={{ background: 'var(--blue)' }} />{done} of {total} milestones complete</span>} />
      <div className="egt-track">
        {PHASES.map((p) => { const pct = phasePct(S, p); return (
          <button type="button" key={p.id} className={cn('egt-tp', open === p.id && 'on', pct === 100 && 'done')} onClick={() => setOpen(p.id)} data-testid={`egt-track-${p.id}`}><i /><small>{p.id} · {p.when.split(' → ')[0]}</small><b>{p.name}</b></button>
        ); })}
      </div>
      <Sub>Phase detail</Sub>
      <div className="egt-phases">{PHASES.map((p) => <Phase key={p.id} ph={p} S={S} open={open === p.id} onOpen={() => setOpen(open === p.id ? null : p.id)} onToggle={onToggle} />)}</div>
    </div>
  );
}
